import { Component, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { State } from 'vuex-class'

import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Policy } from '@/modules/Policy/interfaces/policy'
import { SnackType } from '@/interfaces/snack'
import { showErrors, showNotification } from '@/utils/snack'
import { ApolloResponse } from '@/interfaces/apollo'
import { dateTimeToDisplay, dateToDisplay, dateToNow } from '@/utils/dateTime'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import PolicyDetailDetail from '@/modules/Policy/views/detail.vue'
import AppTabs from '@/components/AppTabs.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppTextSearch from '@/components/AppTextSearch.vue'
import AppCheckbox from '@/components/AppCheckbox.vue'
import AppRadio from '@/components/AppRadio.vue'
import { RecordType } from '@/modules/RecordType/interfaces/record-type'
import { Label } from '@/modules/Label/interfaces/label'
import RecordTypesHandler from '@/mixins/record-types'
import PolicyDialog from '@/modules/Policy/scripts/policy-dialog'
import { getPolicyById, updatePolicyLabels } from '@/modules/Policy/gql/policy.gql'
import { getLabels } from '@/modules/Label/gql/label-gql'
import { policyStatus, policyType, policyStatusClass } from '@/modules/Policy/helpers/policy'
import { caseTypeClass, caseType } from '@/modules/Case/helpers/case'
import { Case, CaseType } from '@/modules/Case/interfaces/case'
import PolicyForm from '@/modules/Policy/components/PolicyForm.vue'
import { dispatchSetIsLoading } from '../../../store/dispatches'

@Component({
  components: {
    AppBreadcrumbs,
    PolicyDetailDetail,
    AppTabs,
    AppDialog,
    AppTextSearch,
    AppCheckbox,
    AppRadio,
    PolicyForm
  }
})

export default class PolicyDetail extends mixins(PolicyDialog, RecordTypesHandler) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Insurance Cases'
    },
    {
      text: 'Policies',
      to: { name: 'PoliciesList' }
    }
  ]

  policy: Policy | null = null

  tabActive = 0
  tabItems = [
    {
      title: 'Details',
      route: 'PolicyDetailDetail'
    },
    {
      title: 'ACORD Form',
      route: 'PolicyAcordForm'
    },
    {
      title: 'Related',
      route: 'PolicyDetailRelated'
    },

    {
      title: 'Payment',
      route: 'PolicyDetailCollection'
    },
    {
      title: 'Source',
      route: 'PolicyDetailSource'
    },
    {
      title: 'Addtional',
      route: 'PolicyDetailAdditional'
    },
    {
      title: 'Endorsement',
      route: 'PolicyDetailEndorsement'
    },
    {
      title: 'DocuSign',
      route: 'PolicyDetailDocuSign'
    },
    {
      title: 'Claim',
      route: 'PolicyDetailClaim'
    },
    {
      title: 'History',
      route: 'PolicyDetailHistory'
    }
  ]

  disableConfirm = false
  activeDelete = false

  recordTypeIndex = 0

  initState = true

  editBlock = ''

  showLabelOptions = false

  showCases = true

  labels: Label[] = []

  policyLabels: Label[] = []

  policyStatusClass = policyStatusClass
  caseTypeClass = caseTypeClass
  caseType = caseType

  dateToNow = dateToNow

  @State(state => state.app.isFold) isFold!: boolean

  get caseDetailRoute () {
    if (!this.policy || this.policy.caseId) {
      return {}
    }
    return {
      name: 'CaseDetailDetail',
      params: {
        id: this.policy.caseId
      }
    }
  }

  get tabTitles () {
    return this.tabItems.map(item => item.title)
  }

  get recordType () {
    if (this.$R.isEmpty(this.recordTypes) || !this.policy) {
      return null
    }
    const recordTypeName = this.policy.recordType.name
    return this.$R.find((recordType: RecordType) => {
      return recordType.name === recordTypeName
    })(this.recordTypes)
  }

  dateTimeToDisplay = dateTimeToDisplay
  dateToDisplay = dateToDisplay

  policyStatus = policyStatus
  policyType = policyType

  sortByIdCaseInsensitive = this.$R.sortBy(this.$R.compose(this.$R.toLower, this.$R.prop('id')));

  async mounted () {
    const { name } = this.$route
    const index = this.$R.findIndex((item: any) => item.route === name)(this.tabItems)
    this.tabActive = index
    await this.getLabels()
    this.getPolicy()
    await this.getRecordTypes('POLICY')
  }

  @Watch('$route.params.id')
  async onRouteParamUpdate (val: string) {
    await this.getLabels()
    this.initState = true
    this.breadcrumbs.pop()
    this.getPolicy()
  }

  async getPolicy () {
    const policyId = Number(this.$route.params.id)
    dispatchSetIsLoading(true)
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getPolicyById,
        variables: {
          policyId
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.policy = result.data.getPolicyById as Policy
      if (this.policy.payments.length > 0) {
        this.policy.payments = this.sortByIdCaseInsensitive(this.policy.payments)
      }

      if (this.policy.commissions.length > 0) {
        this.policy.commissions = this.sortByIdCaseInsensitive(this.policy.commissions)
      }

      const that = this
      this.policyLabels = JSON.parse(JSON.stringify(this.policy.labels))
      if (this.policyLabels.length > 0) {
        this.$R.forEach((label: Label) => {
          that.$R.forEach((labelItem: Label) => {
            if (Number(label.id) === Number(labelItem.id)) {
              label.checked = true
            }
          }, that.policyLabels)
        }, this.labels)
      }

      const caseId = this.policy.caseId || '--'

      if (this.initState) {
        this.breadcrumbs.push({
          text: `${this.policy.policyName} (Case ID: ${caseId})`
        })
        this.initState = false
      }
    } catch (e) {
      showErrors(e.graphQLErrors)
    } finally {
      dispatchSetIsLoading(false)
    }
  }

  clickTabHandler (index: number) {
    const item = this.tabItems[index]
    if (item.route && item.route !== this.$route.name) {
      this.$router.push({
        name: item.route
      })
    }
  }

  async deletePolicyConfirm () {
    await this.deletePolicyPost()
    this.$router.replace({
      name: 'PoliciesList'
    })
  }

  updatePolicy (step: number) {
    this.showPolicyEditDialog(step, this.policy as Policy)
  }

  routeToAccountDetail () {
    if (this.policy && this.policy.account) {
      this.$router.push({
        name: 'AccountDetailDetail',
        params: {
          id: this.policy.account.id + ''
        }
      })
    }
  }

  routeToContactDetail () {
    if (this.policy && this.policy.primaryContact) {
      this.$router.push({
        name: 'ContactDetailDetail',
        params: {
          id: this.policy.primaryContact.id + ''
        }
      })
    }
  }

  editPolicyInfo () {
    this.editBlock = 'policyInfo'
  }

  async getLabels () {
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getLabels,
        variables: {
          type: 'POLICY'
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.labels = result.data.getLabels
      this.$R.forEach((label: Label) => {
        label.checked = false
      }, this.labels)
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  checkLabel (val: Label) {
    if (!this.policy) {
      return
    }
    const valIndex = this.policyLabels.findIndex((label: Label) => {
      return label.content === val.content
    })
    if (val.checked) {
      if (this.policyLabels.length > 0) {
        this.policyLabels.splice(valIndex, 1)
      }
    } else {
      this.policyLabels.push(val)
    }
    val.checked = !val.checked
  }

  async hideLabelOption () {
    this.showLabelOptions = false
    if (!this.policy) {
      return
    }
    const labelIds = this.policy.labels.map((label: { id: any }) => Number(label.id))
    const policyLabelIds = this.policyLabels.map((label: { id: any }) => Number(label.id))
    var isSame = labelIds.length === policyLabelIds.length && labelIds.sort().toString() === policyLabelIds.sort().toString()
    if (!isSame) {
      await this.editPolicyLabel()
    }
  }

  async editPolicyLabel () {
    const { policyLabels, policy } = this
    if (!policy) {
      return
    }
    const { id } = policy

    const labelIds = policyLabels.map((label: { id: any }) => Number(label.id))

    try {
      const res = await this.$apollo.mutate({
        mutation: updatePolicyLabels,
        variables: {
          labelIds,
          policyId: Number(id)
        }
      })
      showNotification({
        msg: 'Update the policy label successfully',
        type: SnackType.Success
      })
      this.getPolicy()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  routeToLabelList () {
    if (this.policy && this.policy.primaryContact) {
      this.$router.push({
        name: 'LabelListType',
        params: {
          type: 'POLICY'
        }
      })
    } else {
      showNotification({
        msg: 'Add priamry contact first',
        type: SnackType.Success
      })
    }
  }

  routeToCase (caseInfo: Case) {
    return caseInfo.type === CaseType.ENDORSEMENT
      ? {
        name: 'CaseDetailEndorsement', params: { id: caseInfo.id }
      }
      : {
        name: 'CaseDetailDetail', params: { id: caseInfo.id }
      }
  }
}
